import { BoundingBox, Vec3 } from 'playcanvas';

type EnginePose = {
    name: string;
    position: Vec3;
    rotation: any;
};

class CameraBounds {
    boundingBox: BoundingBox | null = null;
    enabled: boolean = true;
    expansionFactor: number = 1.5; // Add padding to the bounds...

    /**
     * Update bounds from loaded camera poses
     */
    updateFromPoses(poses: EnginePose[]) {
        if (!poses || poses.length === 0) {
            this.boundingBox = null;
            return;
        }

        // Calculate bounding box from camera positions
        const min = new Vec3(Infinity, Infinity, Infinity);
        const max = new Vec3(-Infinity, -Infinity, -Infinity);

        for (const pose of poses) {
            const pos = pose.position;
            min.x = Math.min(min.x, pos.x);
            min.y = Math.min(min.y, pos.y);
            min.z = Math.min(min.z, pos.z);
            max.x = Math.max(max.x, pos.x);
            max.y = Math.max(max.y, pos.y);
            max.z = Math.max(max.z, pos.z);
        }

        // Create bounding box
        const center = new Vec3();
        center.add2(min, max).mulScalar(0.5);

        const halfExtents = new Vec3();
        halfExtents.sub2(max, min).mulScalar(0.5);

        // Apply expansion factor
        halfExtents.mulScalar(this.expansionFactor);

        this.boundingBox = new BoundingBox(center, halfExtents);
    }

    /**
     * Clamp a position to stay within bounds
     */
    clampPosition(position: Vec3): Vec3 {
        if (!this.enabled || !this.boundingBox) {
            return position;
        }

        const clamped = position.clone();
        const min = new Vec3();
        const max = new Vec3();

        min.sub2(this.boundingBox.center, this.boundingBox.halfExtents);
        max.add2(this.boundingBox.center, this.boundingBox.halfExtents);

        clamped.x = Math.max(min.x, Math.min(max.x, clamped.x));
        clamped.y = Math.max(min.y, Math.min(max.y, clamped.y));
        clamped.z = Math.max(min.z, Math.min(max.z, clamped.z));

        return clamped;
    }

    /**
     * Clear bounds (disables restrictions)
     */
    clear() {
        this.boundingBox = null;
    }

    /**
     * Check if bounds are active
     */
    isActive(): boolean {
        return this.enabled && this.boundingBox !== null;
    }
}

export { CameraBounds };
export type { EnginePose };
